import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BadgeCheck, KeyRound } from "lucide-react";
import { platformApi, type LicenseStatus } from "../lib/api";

type BadgeTone = "ok" | "warn" | "bad";

/**
 * Top-bar license indicator. Mirrors operate-ui's LicenseBadge but
 * themed for curate's indigo/violet palette. Quietly returns null when
 * `/api/v1/license/status` 404s (license auto-config disabled) so the
 * page header stays clean for license-off deployments.
 */
async function fetchLicense(): Promise<LicenseStatus> {
  const { data } = await platformApi.get<LicenseStatus>("/license/status");
  return data;
}

function toneFor(lic: LicenseStatus): BadgeTone {
  if (!lic.ok) return "bad";
  if (lic.daysRemaining <= 14) return "warn";
  return "ok";
}

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function LicenseBadge() {
  const { data: lic } = useQuery<LicenseStatus>({
    queryKey: ["license-status"],
    queryFn: fetchLicense,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(ev: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) setOpen(false);
    }
    function onEsc(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!lic) return null;

  const tone = toneFor(lic);
  const toneClass =
    tone === "ok"
      ? "bg-curate-success/10 text-curate-success"
      : tone === "warn"
      ? "bg-curate-warn/10 text-curate-warn"
      : "bg-curate-danger/10 text-curate-danger";

  const label = lic.ok ? `Licensed · ${lic.daysRemaining}d` : "License issue";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={
          lic.ok
            ? `${lic.customerName ?? "Licensed"} · expires ${formatDate(lic.expiresAt)}`
            : lic.failureMessage ?? "License invalid"
        }
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium transition-colors ${toneClass}`}
      >
        {tone === "bad" ? <AlertTriangle size={11} /> : <BadgeCheck size={11} />}
        {label}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="License details"
          className="absolute right-0 z-30 mt-2 w-80 rounded-lg border border-curate-slate/20 bg-white p-4 text-xs text-curate-ink shadow-elev"
        >
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-curate-ink">
            <KeyRound size={14} />
            License
          </div>

          {lic.ok ? (
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5">
              <dt className="text-curate-slate">Issued to</dt>
              <dd className="font-medium">{lic.customerName ?? "—"}</dd>
              <dt className="text-curate-slate">Expires</dt>
              <dd>
                {formatDate(lic.expiresAt)}
                <span className="ml-1 text-curate-slate">({lic.daysRemaining}d)</span>
              </dd>
              <dt className="text-curate-slate">Issued</dt>
              <dd>{formatDate(lic.issuedAt)}</dd>
            </dl>
          ) : (
            <div className="rounded-md bg-curate-danger/10 px-3 py-2 text-curate-danger">
              <div className="font-semibold">Invalid: {lic.failureReason ?? "unknown"}</div>
              {lic.failureMessage && <div className="mt-1">{lic.failureMessage}</div>}
            </div>
          )}

          <div className="mt-3 border-t border-curate-slate/15 pt-2">
            <div className="text-2xs uppercase tracking-wide text-curate-slate">
              Hardware fingerprint
            </div>
            <code className="mt-1 block break-all rounded bg-curate-mist px-2 py-1 font-mono text-[11px] text-curate-ink">
              {lic.fingerprint}
            </code>
            <p className="mt-1 text-2xs text-curate-slate">
              Email this to support@cloudcraftlabs.com for renewals.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
